/**
 * AiModelDetailPage — iter-232c (Feb 2026)
 *
 * Route: /control/ai/models/:agentCode
 * Purpose: Full detail page for a single AI model. Replaces the old modal
 *          "edit" dialog for the admin flow.
 *
 * Top-of-page toolbar (4 buttons per user request):
 *   • Stats       — opens a modal with THIS bot's stats
 *   • Edit        — opens/closes inline edit mode
 *   • Pause/Resume — toggles paused; Resume ALSO promotes status from
 *                    'deprecated' back to 'active' so the outside label
 *                    reflects reality (bug fix: previously it stayed "متوقف").
 *   • Delete      — HARD delete (removes the doc from Mongo entirely). Red
 *                   button, strong confirm required.
 *
 * Ownership: iter-232c removed the "Owner Agent" concept. Bots are now
 * shared across every user of the platform indicated by their platform
 * code (e.g. platform_code=CGB → served to every CGB user).
 *
 * Bilingual fields (name / description / capabilities) each carry a
 * "AR → EN" / "EN → AR" translation button powered by
 * `POST /api/utils/translate` (Gemini direct).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2, Save, Pencil, X, PauseCircle, PlayCircle, BarChart3, Lock,
  MessageSquare, Paperclip, Languages, Sparkles, Bot, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getAuthHeaders } from '../utils/authHeaders';
import { TranslateButton } from '../components/ai-control/TranslateButton';
import { AiModelPicker } from '../components/ai-control/AiModelPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AiModelDetailPage() {
  const nav = useNavigate();
  const { agentCode } = useParams();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (a, e) => (isAr ? a : e);

  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const [form, setForm] = useState({
    name_ar: '',
    name_en: '',
    display_name_ar: '',
    display_name_en: '',
    description_ar: '',
    description_en: '',
    capabilities_ar_str: '',
    capabilities_en_str: '',
    model_used: '',
    thinking_level: 'minimal',
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/ai-registry/${encodeURIComponent(agentCode)}`,
        { headers: getAuthHeaders() },
      );
      const d = res.data?.data || {};
      setBot(d);
      setForm({
        name_ar:             d.name_ar || '',
        name_en:             d.name_en || '',
        display_name_ar:     d.display_name_ar || '',
        display_name_en:     d.display_name_en || '',
        description_ar:      d.description_ar || '',
        description_en:      d.description_en || '',
        capabilities_ar_str: (d.capabilities_ar || []).join(', '),
        capabilities_en_str: (d.capabilities_en || []).join(', '),
        model_used:          d.model_used || '',
        thinking_level:      d.thinking_level || 'minimal',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('فشل تحميل البوت', 'Failed to load bot'));
      nav('/control/ai?panel=directory');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [agentCode]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name_ar:         form.name_ar,
        name_en:         form.name_en,
        display_name_ar: form.display_name_ar,
        display_name_en: form.display_name_en,
        description_ar:  form.description_ar,
        description_en:  form.description_en,
        capabilities_ar: form.capabilities_ar_str.split(',').map((s) => s.trim()).filter(Boolean),
        capabilities_en: form.capabilities_en_str.split(',').map((s) => s.trim()).filter(Boolean),
        model_used:      form.model_used || undefined,
        thinking_level:  form.thinking_level || undefined,
      };
      await axios.put(`${BACKEND_URL}/api/admin/ai-registry/${encodeURIComponent(agentCode)}`, payload, { headers: getAuthHeaders() });
      toast.success(t('تم الحفظ ✓', 'Saved ✓'));
      setEditMode(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  // iter-232c — Fix for the ASB-AI-CLA-ASB-001 report: previously we only
  // toggled `paused`; the outside directory shows `status`, so a deprecated
  // bot stayed labelled "متوقف" even after Resume. Now Resume ALSO promotes
  // status → 'active' when appropriate.
  // iter-299e — Also promote 'draft' → 'active' on Resume so legacy drafts
  // escape the retired draft state without needing to open the full form.
  const togglePause = async () => {
    if (!bot) return;
    const nextPaused = !bot.paused;
    const patch = { paused: nextPaused };
    if (!nextPaused && (bot.status === 'deprecated' || bot.status === 'draft')) {
      patch.status = 'active';
    }
    try {
      await axios.put(`${BACKEND_URL}/api/admin/ai-registry/${encodeURIComponent(agentCode)}`, patch, { headers: getAuthHeaders() });
      toast.success(nextPaused ? t('تم إيقاف البوت', 'Bot paused') : t('تم تشغيل البوت', 'Bot resumed'));
      if (nextPaused) setEditMode(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('فشل التبديل', 'Toggle failed'));
    }
  };

  const hardDelete = async () => {
    if (!bot) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/ai-registry/${encodeURIComponent(agentCode)}/hard`,
        { headers: getAuthHeaders() },
      );
      toast.success(t(`تم حذف ${agentCode} نهائياً`, `${agentCode} deleted permanently`));
      nav('/control/ai?panel=directory');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
      setDeleting(false);
    }
  };

  const disabled = !editMode || (bot?.paused ?? false);

  if (loading || !bot) {
    return (
      <DashboardLayout title="AI Model" onBack={() => nav('/control/ai?panel=directory')}>
        <div className="flex items-center justify-center py-24 text-white/50">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const breadcrumb = [
    { labelAr: 'الرئيسية', labelEn: 'Home', path: '/dashboard' },
    { labelAr: 'التحكم بالـ AI', labelEn: 'AI Control', path: '/control/ai' },
    { labelAr: 'التحكم بنماذج الذكاء الاصطناعي', labelEn: 'AI Models Control', path: '/control/ai?panel=directory' },
    { labelAr: (isAr ? bot.name_ar : bot.name_en) || bot.agent_code, labelEn: (isAr ? bot.name_ar : bot.name_en) || bot.agent_code },
  ];

  return (
    <DashboardLayout
      title="AI Model"
      titleEn="AI Model"
      screenCode="AI-002"
      backPath="/control/ai?panel=directory"
      onBack={() => nav('/control/ai?panel=directory')}
      breadcrumb={breadcrumb}
    >
      <section className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 ${bot.paused ? 'opacity-70' : ''}`} data-testid="ai-model-detail">
        {/* Header — identity + top toolbar */}
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-[#151530] to-[#0f0f24]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-14 h-14 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/30 flex items-center justify-center flex-shrink-0">
                <Bot className="w-7 h-7 text-[#d4af37]" />
              </div>
              <div className="min-w-0">
                <div className="text-white font-bold text-xl truncate">
                  {isAr ? (bot.name_ar || bot.name_en) : (bot.name_en || bot.name_ar)}
                </div>
                <div className="text-[12px] text-white/40 font-mono" dir="ltr">{bot.agent_code}</div>
                {(isAr ? bot.description_ar : bot.description_en) && (
                  <div className="text-sm text-white/60 mt-1 line-clamp-2 max-w-xl">
                    {isAr ? bot.description_ar : bot.description_en}
                  </div>
                )}
              </div>
            </div>

            {/* Top toolbar — 4 buttons: Stats / Edit / Pause / Delete */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setStatsOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-blue-500/15 border border-blue-500/40 text-blue-300 hover:bg-blue-500/25"
                data-testid="ai-model-stats-btn"
              >
                <BarChart3 className="w-4 h-4" />
                {t('إحصائيات', 'Stats')}
              </button>
              {!bot.paused && (
                <button
                  type="button"
                  onClick={() => setEditMode((v) => !v)}
                  className={
                    'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border ' +
                    (editMode
                      ? 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                      : 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25')
                  }
                  data-testid="ai-model-edit-btn"
                >
                  {editMode ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                  {editMode ? t('إلغاء', 'Cancel') : t('تعديل', 'Edit')}
                </button>
              )}
              <button
                type="button"
                onClick={togglePause}
                className={
                  'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border ' +
                  (bot.paused
                    ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25'
                    : 'bg-amber-500/15 border-amber-500/40 text-amber-300 hover:bg-amber-500/25')
                }
                data-testid="ai-model-pause-toggle"
              >
                {bot.paused ? <PlayCircle className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
                {bot.paused ? t('تشغيل', 'Resume') : t('إيقاف', 'Pause')}
              </button>
              {/* iter-232c — Hard delete (red). Opens a strong confirm modal. */}
              <button
                type="button"
                onClick={() => { setConfirmText(''); setConfirmDelete(true); }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border bg-rose-500/15 border-rose-500/40 text-rose-300 hover:bg-rose-500/25"
                data-testid="ai-model-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
                {t('حذف البوت', 'Delete Bot')}
              </button>
            </div>
          </div>
        </motion.div>

        {bot.paused && (
          <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm text-center" data-testid="ai-model-paused-banner">
            {t('البوت موقوف حالياً — كل التعديلات معطلة. اضغط "تشغيل" للاستئناف.',
               'This bot is paused — all edits are disabled. Click "Resume" to continue.')}
          </div>
        )}

        {/* Locked (read-only) fields */}
        <div className="p-4 rounded-2xl border border-white/5 bg-white/[0.02]">
          <div className="text-xs text-white/50 mb-3 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" />
            {t('حقول مقفلة — لا يمكن تعديلها بعد الإنشاء', 'Locked fields — cannot be edited after creation')}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px]">
            <ReadOnlyField label={t('المزوّد', 'Provider')} value={bot.provider_code} />
            <ReadOnlyField label={t('النموذج الحالي (يُعدَّل بالأسفل)', 'Current model (edit below)')} value={`${bot.model_used || '—'} · ${bot.thinking_level || 'minimal'}`} />
            <ReadOnlyField label={t('المنصة', 'Platform')} value={bot.platform_code} />
            <ReadOnlyField label={t('حد شهري', 'Monthly limit')}
                           value={bot.rate_limit_enabled === false ? t('بلا حد', 'Unlimited') : (bot.monthly_call_limit || t('—', '—'))} />
          </div>
          {/* iter-299l — Voice capability chips (always visible, read-only on detail header) */}
          <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-white/40 me-1">
              {t('قدرات الصوت:', 'Voice capabilities:')}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold ${
              (bot.voice_input_enabled !== false)
                ? 'bg-sky-500/15 text-sky-300 border border-sky-500/40'
                : 'bg-white/5 text-white/30 border border-white/10'
            }`}>
              🎤 {(bot.voice_input_enabled !== false)
                ? t('صوت دخول مفعّل · Gemini STT', 'Voice input ON · Gemini STT')
                : t('صوت دخول متوقف', 'Voice input OFF')}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold ${
              (bot.voice_output_enabled !== false)
                ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
                : 'bg-white/5 text-white/30 border border-white/10'
            }`}>
              🔊 {(bot.voice_output_enabled !== false)
                ? t(`رد صوتي · ${bot.voice_name || 'Puck'}`,
                    `Voice reply · ${bot.voice_name || 'Puck'}`)
                : t('رد صوتي متوقف', 'Voice reply OFF')}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/40">
              🧠 {t('موديل نصي:', 'Text model:')} {bot.model_used || '—'}
            </span>
          </div>
        </div>

        {/* Editable form — bilingual with translation buttons */}
        <div className={`space-y-4 ${disabled ? 'pointer-events-none opacity-60' : ''}`}>
          {bot.provider_code === 'GEM' && (
            <FieldGroup title={t('النموذج وسرعة الرد', 'Model & reply speed')}
                        hint={t('القائمة تُسحب مباشرة من Google حسب مفتاحك — التغيير يسري فوراً على البوت', 'Pulled live from Google with your key — applies to the bot immediately after save')}>
              <AiModelPicker providerCode={bot.provider_code} model={form.model_used}
                             thinkingLevel={form.thinking_level} disabled={disabled}
                             onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} />
            </FieldGroup>
          )}
          <FieldGroup title={t('مسمى النموذج (إداري)', 'Model name (Admin)')}
                      hint={t('الاسم اللي يظهر لك هنا فقط', 'Only shown here in admin')}>
            <BilingualRow>
              <Input value={form.name_ar}
                     onChange={(e) => setForm(f => ({ ...f, name_ar: e.target.value }))}
                     placeholder={t('الاسم بالعربية', 'Arabic name')} disabled={disabled}
                     className="bg-[#0a0a0a] border-white/10 text-white rounded-xl flex-1"
                     data-testid="ai-model-name-ar" />
              <TranslateButton sourceText={form.name_ar} source="ar" target="en" isAr={isAr}
                onTranslated={(txt) => setForm(f => ({ ...f, name_en: txt }))}
                testid="ai-model-name-translate-en" />
            </BilingualRow>
            <BilingualRow>
              <Input value={form.name_en}
                     onChange={(e) => setForm(f => ({ ...f, name_en: e.target.value }))}
                     placeholder="English name" dir="ltr" disabled={disabled}
                     className="bg-[#0a0a0a] border-white/10 text-white rounded-xl flex-1"
                     data-testid="ai-model-name-en" />
              <TranslateButton sourceText={form.name_en} source="en" target="ar" isAr={isAr}
                onTranslated={(txt) => setForm(f => ({ ...f, name_ar: txt }))}
                testid="ai-model-name-translate-ar" />
            </BilingualRow>
          </FieldGroup>

          {/* iter-233 — Public persona: how the bot signs its replies to
             end-users. Editable here AND by other-platform agents via
             /api/ai-core/bots/{code}/config. Highlighted in blue. */}
          <FieldGroup
            title={
              <span className="flex items-center gap-2">
                <span className="text-blue-300">{t('الاسم العلني للبوت (يظهر للعملاء)', 'Public persona (shown to end-users)')}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-200 border border-blue-500/30">
                  {t('قابل للتعديل من ايجنت المنصة', 'Editable by platform agent')}
                </span>
              </span>
            }
            hint={t('يُدرج تلقائياً في نظام البوت عند كل استدعاء', 'Injected into the bot system prompt at invoke time')}
          >
            <BilingualRow>
              <Input value={form.display_name_ar}
                     onChange={(e) => setForm(f => ({ ...f, display_name_ar: e.target.value }))}
                     placeholder={t('مثال: مساعد كلاود جي إم للتجار', 'e.g. CloudGM Merchant Assistant')} disabled={disabled}
                     className="bg-[#0a0a0a] border-blue-500/30 text-white rounded-xl flex-1"
                     data-testid="ai-model-display-name-ar" />
              <TranslateButton sourceText={form.display_name_ar} source="ar" target="en" isAr={isAr}
                onTranslated={(txt) => setForm(f => ({ ...f, display_name_en: txt }))}
                testid="ai-model-display-name-translate-en" />
            </BilingualRow>
            <BilingualRow>
              <Input value={form.display_name_en}
                     onChange={(e) => setForm(f => ({ ...f, display_name_en: e.target.value }))}
                     placeholder="e.g. CloudGM Merchant Assistant" dir="ltr" disabled={disabled}
                     className="bg-[#0a0a0a] border-blue-500/30 text-white rounded-xl flex-1"
                     data-testid="ai-model-display-name-en" />
              <TranslateButton sourceText={form.display_name_en} source="en" target="ar" isAr={isAr}
                onTranslated={(txt) => setForm(f => ({ ...f, display_name_ar: txt }))}
                testid="ai-model-display-name-translate-ar" />
            </BilingualRow>
          </FieldGroup>

          <FieldGroup title={t('الوصف (يظهر تحت المسمى)', 'Description (shown under the name)')}>
            <BilingualRow>
              <textarea value={form.description_ar}
                onChange={(e) => setForm(f => ({ ...f, description_ar: e.target.value }))}
                placeholder={t('وصف قصير (عربي)', 'Short description (Arabic)')} rows={2} disabled={disabled}
                className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-xl p-3 text-white text-sm disabled:opacity-50"
                data-testid="ai-model-desc-ar" />
              <TranslateButton sourceText={form.description_ar} source="ar" target="en" isAr={isAr}
                onTranslated={(txt) => setForm(f => ({ ...f, description_en: txt }))}
                testid="ai-model-desc-translate-en" />
            </BilingualRow>
            <BilingualRow>
              <textarea value={form.description_en}
                onChange={(e) => setForm(f => ({ ...f, description_en: e.target.value }))}
                placeholder="Short description (English)" dir="ltr" rows={2} disabled={disabled}
                className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-xl p-3 text-white text-sm disabled:opacity-50"
                data-testid="ai-model-desc-en" />
              <TranslateButton sourceText={form.description_en} source="en" target="ar" isAr={isAr}
                onTranslated={(txt) => setForm(f => ({ ...f, description_ar: txt }))}
                testid="ai-model-desc-translate-ar" />
            </BilingualRow>
          </FieldGroup>

          <FieldGroup title={t('المميزات', 'Capabilities')} hint={t('افصل بفاصلة', 'Comma-separated')}>
            <BilingualRow>
              <textarea value={form.capabilities_ar_str}
                onChange={(e) => setForm(f => ({ ...f, capabilities_ar_str: e.target.value }))}
                placeholder={t('مثال: دردشة، دعم عملاء', 'e.g. دردشة، دعم عملاء')} rows={2} disabled={disabled}
                className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-xl p-3 text-white text-sm disabled:opacity-50"
                data-testid="ai-model-caps-ar" />
              <TranslateButton sourceText={form.capabilities_ar_str} source="ar" target="en" isAr={isAr}
                onTranslated={(txt) => setForm(f => ({ ...f, capabilities_en_str: txt }))}
                testid="ai-model-caps-translate-en" />
            </BilingualRow>
            <BilingualRow>
              <textarea value={form.capabilities_en_str}
                onChange={(e) => setForm(f => ({ ...f, capabilities_en_str: e.target.value }))}
                placeholder="e.g. Chat, Customer support" dir="ltr" rows={2} disabled={disabled}
                className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-xl p-3 text-white text-sm disabled:opacity-50"
                data-testid="ai-model-caps-en" />
              <TranslateButton sourceText={form.capabilities_en_str} source="en" target="ar" isAr={isAr}
                onTranslated={(txt) => setForm(f => ({ ...f, capabilities_ar_str: txt }))}
                testid="ai-model-caps-translate-ar" />
            </BilingualRow>
          </FieldGroup>

          {editMode && !bot.paused && (
            <Button onClick={save} disabled={saving}
              className="w-full bg-[#d4af37] hover:bg-[#c9a227] text-black font-bold gap-2 py-6"
              data-testid="ai-model-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('حفظ التغييرات', 'Save Changes')}
            </Button>
          )}
        </div>
      </section>

      <AnimatePresence>
        {statsOpen && <StatsModal bot={bot} onClose={() => setStatsOpen(false)} t={t} isAr={isAr} />}
        {confirmDelete && (
          <ConfirmDeleteModal
            agentCode={agentCode}
            confirmText={confirmText}
            setConfirmText={setConfirmText}
            onCancel={() => { setConfirmDelete(false); setConfirmText(''); }}
            onConfirm={hardDelete}
            deleting={deleting}
            isAr={isAr}
            t={t}
          />
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}

// ─────────────────────────────────────────────────────────────
function ReadOnlyField({ label, value }) {
  return (
    <div>
      <div className="text-[11px] text-white/40 mb-1">{label}</div>
      <div className="px-3 py-1.5 rounded-lg bg-black/40 border border-white/5 text-white/80 font-mono text-[12px] truncate" dir="ltr">
        {value}
      </div>
    </div>
  );
}

function FieldGroup({ title, hint, children }) {
  return (
    <div>
      <label className="block text-sm text-white/70 mb-2">
        {title}
        {hint && <span className="text-white/40 mx-2 text-xs">· {hint}</span>}
      </label>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

// Wraps a bilingual field + its translate button on the same row.
function BilingualRow({ children }) {
  return <div className="flex items-start gap-2">{children}</div>;
}

// Stats modal
function StatsModal({ bot, onClose, t, isAr }) {
  const stats = bot.usage_stats || {};
  const tasks = bot.task_breakdown || {};
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
      onClick={onClose} data-testid="ai-model-stats-modal">
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-gradient-to-br from-[#0f0f24] to-[#0a0a19] border border-blue-500/30 rounded-2xl p-6"
        dir={isAr ? 'rtl' : 'ltr'}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-300" />
            <h3 className="text-white font-bold text-lg">
              {t(`إحصائيات ${bot.agent_code}`, `Stats — ${bot.agent_code}`)}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white" data-testid="ai-model-stats-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <StatTile icon={MessageSquare} label={t('المحادثات', 'Conversations')}
                    value={tasks.conversations || stats.call_count || 0} color="#60a5fa" />
          <StatTile icon={Paperclip} label={t('مرفقات قُرئت', 'Attachments read')}
                    value={tasks.attachments || 0} color="#c084fc" />
          <StatTile icon={Languages} label={t('ترجمات', 'Translations')}
                    value={tasks.translations || 0} color="#34d399" />
          <StatTile icon={Sparkles} label={t('إجمالي الاستدعاءات', 'Total invokes')}
                    value={stats.call_count || 0} color="#fbbf24" />
        </div>
        <div className="mt-5 pt-4 border-t border-white/5 text-[11px] text-white/40 flex justify-between">
          <span>{t('هذا الشهر', 'This month')}: <b className="text-white">{stats.monthly_calls || 0}</b></span>
          {stats.last_called_at && (
            <span dir="ltr">
              {t('آخر استخدام', 'Last used')}: {new Date(stats.last_called_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB')}
            </span>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function StatTile({ icon: Icon, label, value, color }) {
  return (
    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
      <div className="flex items-center gap-2 mb-1 text-[11px] text-white/50">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        {label}
      </div>
      <div className="text-2xl font-bold text-white font-mono">{value}</div>
    </div>
  );
}

// iter-232c — Hard-delete confirm modal. User must type the bot code
// verbatim to arm the destructive action.
function ConfirmDeleteModal({ agentCode, confirmText, setConfirmText, onCancel, onConfirm, deleting, isAr, t }) {
  const ok = confirmText.trim() === agentCode;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[85] flex items-center justify-center p-4"
      onClick={onCancel} data-testid="ai-model-delete-modal">
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-gradient-to-br from-[#1a0f0f] to-[#0a0a0a] border border-rose-500/40 rounded-2xl p-6"
        dir={isAr ? 'rtl' : 'ltr'}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center">
            <Trash2 className="w-6 h-6 text-rose-300" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">{t('حذف البوت نهائياً', 'Delete bot permanently')}</h3>
            <p className="text-xs text-rose-300/80">
              {t('هذا الإجراء لا يمكن التراجع عنه', 'This action cannot be undone')}
            </p>
          </div>
        </div>
        <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-sm text-rose-100 mb-4">
          {t(
            `سيتم حذف البوت "${agentCode}" وكل بياناته من قاعدة البيانات. للتأكيد، اكتب كود البوت بالأسفل:`,
            `Bot "${agentCode}" and all its data will be removed from the database. To confirm, type the bot code below:`,
          )}
        </div>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={agentCode}
          dir="ltr"
          className="bg-[#0a0a0a] border-rose-500/40 text-white rounded-xl mb-4 font-mono"
          data-testid="ai-model-delete-confirm-input"
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} className="text-white/60 hover:bg-white/5"
                  data-testid="ai-model-delete-cancel">
            {t('إلغاء', 'Cancel')}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!ok || deleting}
            className="bg-rose-500 hover:bg-rose-600 text-white gap-2 disabled:opacity-40"
            data-testid="ai-model-delete-confirm"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {t('حذف نهائي', 'Delete forever')}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
